import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useFeature } from '@treams/treams-hooks/useFeature';
import { useLocalize } from '@treams/treams-hooks/useLocalize';
import { useViewer } from '@treams/treams-hooks/useViewer';
import { useViewerRoles } from '@treams/treams-hooks/useViewerRoles';
import * as graphql from '@treams/treams-network';
import { Button } from '@treams/treams-ui/common/button/Button';
import { createTimeMachine } from '@treams/treams-ui/common/DateIntervalNavigation';
import { GraphQLErrorMessage } from '@treams/treams-ui/common/GraphQLErrorMessage';
import { Jumbo } from '@treams/treams-ui/common/Jumbo';
import { Paragraph } from '@treams/treams-ui/common/Text';
import { QuillText } from '@treams/treams-ui/form/controls/QuillText';

import { useJobTitleName } from '../../job/useJobTitleName';
import CareerDetailHeader from '../components/CareerDetailHeader';
import GrowthPathAllCompetencies from '../components/GrowthPathAllCompetencies';
import GrowthPathDetailBody, {
	GrowthPathDetailBodySkeleton,
	GrowthPathEmpty,
} from '../components/GrowthPathDetailBody';
import GrowthPathHeader from '../components/GrowthPathHeader';
import LabelQueryWrapper from '../components/LabelQueryWrapper';
import { useGrowthPathContext } from '../contexts/GrowthPathContext';
import {
	growthPathDataMappingFromUser,
	growthPathJobPositions,
	receivedFeedbackFromUser,
	unavailableFeedback,
} from '../GrowthPathConverter';
import {
	useGrowthPathFeedbackQuery,
	useGrowthPathUserCompetenciesQuery,
} from '../hooks/useGrowthPathScale';
import { GrowthPathDetailContainer } from '../pages/GrowthPath';

const MyJobTitleDetail: FC = () => {
	const { t } = useTranslation();
	const viewer = useViewer();
	const { isManager, isAdministrator, isWorkspaceSuperuser } = useViewerRoles();
	const history = useHistory();
	const { localize } = useLocalize();
	const jobTitleName = useJobTitleName();
	const multiRoleConversion = useFeature(graphql.FeatureName.MultiRoleConversion);
	const hasJobCompetenciesExplorer = useFeature(graphql.FeatureName.JobCompetenciesExplorer);

	const { userID, jobTitleID } = useGrowthPathContext();
	const overviewFeedbackEnabled = useFeature(graphql.FeatureName.GrowthPathOverviewFeedback);
	const overviewFeedback = jobTitleID === 'all' && overviewFeedbackEnabled;
	const [timeMachine, setTimeMachine] = useState(() => createTimeMachine('year', new Date()));

	const { refetch: userRefetch } = graphql.useUserQuery({
		variables: { id: userID ?? viewer!.id },
		fetchPolicy: 'cache-and-network',
	});

	const {
		data: userCompetenciesData,
		loading: userCompetenciesLoading,
		error: userCompetenciesError,
		scale,
	} = useGrowthPathUserCompetenciesQuery(
		{ id: userID ?? viewer!.id },
		{ fetchPolicy: 'cache-and-network' },
	);

	const {
		data: feedbackData,
		previousData: feedbackPreviousData,
		loading: feedbackLoading,
		error: feedbackError,
	} = useGrowthPathFeedbackQuery(
		{
			id: userID ?? viewer!.id,
			startDate: timeMachine.current[0],
			endDate: timeMachine.current[1],
		},
		{
			skip: !overviewFeedback,
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all',
		},
	);

	const feedbackResult = feedbackData ?? (feedbackError ? undefined : feedbackPreviousData);

	const {
		data: careersData,
		loading: careersLoading,
		error: careersError,
	} = graphql.useCareersQuery({
		variables: { isArchived: false, first: 1000 },
		fetchPolicy: 'cache-and-network',
	});

	const feedbackPending = overviewFeedback && feedbackLoading && !feedbackResult;
	const loading = careersLoading || userCompetenciesLoading || feedbackPending;

	const career = careersData?.careers.edges.find((edge) =>
		edge.node.jobTitles.some((jobTitle) => jobTitle.id === jobTitleID),
	)?.node;

	const jobPositions = userCompetenciesData
		? growthPathJobPositions(userCompetenciesData.user)
		: [];
	const allJobPositionsExcluded =
		!!userCompetenciesData?.user.jobPositions.length && jobPositions.length === 0;

	const jobPosition = jobPositions.find((jobPosition) => jobPosition.jobTitle.id === jobTitleID);

	const allCompetencies = jobPositions.flatMap((jobPosition) => jobPosition.competencies);

	const jobCompetencies = jobPosition ? jobPosition.competencies : allCompetencies;

	const hasDescription =
		jobPosition?.jobTitle?.description?.[0]?.text?.ops?.length &&
		jobPosition?.jobTitle?.description?.[0]?.text?.ops?.length > 0;

	const linkedJobTitle = career?.jobTitles.find(
		(jobTitle) => jobPosition?.jobTitle?.id === jobTitle.id,
	);

	const canTerminateJob = isManager || isAdministrator || isWorkspaceSuperuser;

	const [updateJobPosition] = graphql.useUpdateJobPositionMutation();

	const terminateJobPosition = async () => {
		if (!jobPosition) {
			return;
		}

		await updateJobPosition({
			variables: {
				input: {
					id: jobPosition.id,
					jobTitleID: jobPosition.jobTitle.id,
					startDate: jobPosition.startDate,
					userID: userID,
					endDate: new Date(),
					externalReferenceID: jobPosition.externalReferenceID,
					jobLevelID: jobPosition.jobLevel?.id,
				},
			},
		});

		await userRefetch();

		history.push(`/users/${userID}/growth-path/my-job-title/all`);
	};

	const growthPathData = useMemo(() => {
		if (!userCompetenciesData) {
			return undefined;
		}

		return growthPathDataMappingFromUser(userCompetenciesData.user, jobTitleID, scale);
	}, [userCompetenciesData, jobTitleID, scale]);

	const receivedFeedback = useMemo(() => {
		if (!overviewFeedback || !userCompetenciesData) {
			return undefined;
		}

		if (feedbackResult) {
			return receivedFeedbackFromUser(feedbackResult.user);
		}

		if (feedbackError) {
			return unavailableFeedback(userCompetenciesData.user);
		}

		return undefined;
	}, [
		overviewFeedback,
		userCompetenciesData,
		feedbackResult,
		feedbackError,
		receivedFeedbackFromUser,
		unavailableFeedback,
	]);

	if (loading) {
		return <MyJobTitleDetailSkeleton />;
	}
	if (careersError) {
		return <GraphQLErrorMessage error={careersError.graphQLErrors[0]} />;
	}
	if (userCompetenciesError) {
		return <GraphQLErrorMessage error={userCompetenciesError.graphQLErrors[0]} />;
	}

	const defaultPageTitle = !hasJobCompetenciesExplorer
		? t('sidebar.competencies')
		: t('sidebar.growthPath');

	const overview = !jobPosition;
	const title = jobPosition ? jobTitleName(jobPosition.jobTitle) : defaultPageTitle;

	return (
		<GrowthPathDetailContainer>
			<GrowthPathHeader
				title={title}
				description={overview ? t('my.jobTitles.detail.description') : undefined}
				editable={overview && !userID}
				actions={
					jobTitleID !== 'all' && multiRoleConversion && canTerminateJob ? (
						<Button
							type="destructive"
							title={t('career.detail.removeRole')}
							onClick={terminateJobPosition}
						/>
					) : undefined
				}
				extra={
					jobTitleID && career ? (
						<CareerDetailHeader career={career} linkedJobTitle={linkedJobTitle} />
					) : undefined
				}
			>
				{jobPosition && hasDescription && jobPosition.jobTitle.description && (
					<Paragraph py={2}>
						<QuillText value={localize(jobPosition.jobTitle.description).text} />
					</Paragraph>
				)}
			</GrowthPathHeader>
			{growthPathData && jobPositions.length > 0 ? (
				<LabelQueryWrapper jobCompetencies={jobCompetencies} data={userCompetenciesData}>
					{overviewFeedback && receivedFeedback ? (
						<GrowthPathAllCompetencies
							key={timeMachine.urlSafeCurrent.join('|')}
							data={growthPathData}
							feedback={receivedFeedback}
							timeMachine={timeMachine}
							onTimeMachineChange={setTimeMachine}
						/>
					) : (
						<GrowthPathDetailBody data={growthPathData} />
					)}
				</LabelQueryWrapper>
			) : allJobPositionsExcluded ? (
				<GrowthPathEmpty
					title={t('my.jobTitles.detail.excluded.title')}
					description={t('my.jobTitles.detail.excluded.description')}
				/>
			) : (
				<GrowthPathEmpty
					title={t('my.jobTitles.detail.empty.title')}
					description={t('my.jobTitles.detail.empty.description')}
				/>
			)}
		</GrowthPathDetailContainer>
	);
};

const MyJobTitleDetailSkeleton = () => {
	return (
		<>
			<Jumbo.Skeleton wide />
			<GrowthPathDetailBodySkeleton />
		</>
	);
};

export default MyJobTitleDetail;
